import React, { useState, useEffect } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  InputGroup,
  Image,
  Spinner,
  Badge,
  Alert,
  Pagination,
  Card,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const EmployeeDetailsUnapproved = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [imageType, setImageType] = useState("");
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showCertificateListModal, setShowCertificateListModal] =
    useState(false);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const [currentCertificateName, setCurrentCertificateName] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false)

  const companyKey = sessionStorage.getItem("companyKey");
  const isMobile = window.innerWidth < 768;
  const navigate = useNavigate();

  const display = (val) => (!val || val === "null" || val === "" ? "N/A" : val);

  const formatDate = (date) =>
    !date || date === "null" ? "N/A" : new Date(date).toLocaleDateString();

  useEffect(() => {
    const userId = sessionStorage.getItem("userMobile");
    if (!userId) navigate("/signIn");
  }, [navigate]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { data } = await axios.get(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails"
        );
        const filteredData = data.filter((d) => d.companyKey === companyKey);
        const unapproved = filteredData
          .reverse()
          .filter(
            (emp) =>
              emp.approvalStatus === "pending" ||
              emp.approvalStatus === "false" ||
              emp.approvalStatus === "null" ||
              emp.approvalStatus === "" ||
              !emp.approvalStatus
          );
        setEmployees(unapproved);
        setFilteredEmployees(unapproved);
        setLoading(false);
      } catch (err) {
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Error",
          message: `Failed to fetch employees: ${err.message}`,
          duration: 2500, // auto-close in 5s
        });
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // Pagination
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const reversed = [...filteredEmployees].reverse();
  const currentEmployees = reversed.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredEmployees.length / itemsPerPage));
  }, [filteredEmployees, itemsPerPage]);

  useEffect(() => {
    if (!searchTerm && !startDateFilter && !endDateFilter) {
      setFilteredEmployees(employees);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = employees.filter((emp) => {
        const searchMatch =
          !searchTerm ||
          Object.values(emp).some(
            (v) => v && v.toString().toLowerCase().includes(term)
          );
        let dateMatch = true;
        if (startDateFilter || endDateFilter) {
          if (emp.createdAt && emp.createdAt !== "null") {
            try {
              const [day, month, year] = emp.createdAt.split("-");
              const empDate = new Date(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day)
              );

              if (startDateFilter) {
                const startDate = new Date(startDateFilter);
                startDate.setHours(0, 0, 0, 0);
                dateMatch = dateMatch && empDate >= startDate;
              }

              if (endDateFilter) {
                const endDate = new Date(endDateFilter);
                endDate.setHours(23, 59, 59, 999);
                dateMatch = dateMatch && empDate <= endDate;
              }
            } catch (error) {
              console.error("Error parsing date:", emp.createdAt, error);
              dateMatch = false;
            }
          } else {
            dateMatch = false;
          }
        }

        return searchMatch && dateMatch;
      });

      setFilteredEmployees(filtered);
      setCurrentPage(1);
    }
  }, [searchTerm, employees, startDateFilter, endDateFilter]);

  // Export to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredEmployees);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Unapproved Employees");
    XLSX.writeFile(workbook, "Unapproved_Employees.xlsx");
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Unapproved Employee Details", 14, 10);
    doc.autoTable({
      head: [["Emp ID", "Full Name", "Mobile", "Department", "Job Title"]],
      body: filteredEmployees.map((e) => [
        display(e.employeeId),
        display(e.fullName),
        display(e.personalMobile),
        display(e.department),
        display(e.jobTitle),
      ]),
      startY: 20,
    });
    doc.save("Unapproved_Employees.pdf");
  };

  const getISTDateTime = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(now.getTime() + istOffset);

    const day = String(istTime.getUTCDate()).padStart(2, "0");
    const month = String(istTime.getUTCMonth() + 1).padStart(2, "0");
    const year = istTime.getUTCFullYear();

    const hours = String(istTime.getUTCHours()).padStart(2, "0");
    const minutes = String(istTime.getUTCMinutes()).padStart(2, "0");
    const seconds = String(istTime.getUTCSeconds()).padStart(2, "0");

    const date = `${day}-${month}-${year}`;
    const time = `${hours}:${minutes}:${seconds}`;

    return { date, time };
  };

  const handleApprove = async () => {
    try {
      const { date: approvedDate, time: approvedTime } = getISTDateTime();

      const updated = {
        ...currentEmployee,
        approvalStatus: "true",
        approvedDate,
        approvedTime,
      };

      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails/${currentEmployee.id}`,
        updated
      );
      doAuditLog(
        "Update",
        "Employees Detail Approved",
        currentEmployee,
        updated
      );
      const updatedList = employees.filter((e) => e.id !== currentEmployee.id);
      setEmployees(updatedList);
      setFilteredEmployees(updatedList);
      setShowEditModal(false);
      setShowViewModal(false);
      setCurrentEmployee(null);

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Employee Approved",
        message: "Employee Approved Successfully",
        duration: 2500, // auto-close in 2.5s
      });
    } catch (err) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Approval Failed",
        message: `Failed to approve employee: ${err.message}`,
        duration: 2500, // auto-close in 2.5s
      });

    }
  };

  const handleView = (emp) => {
    setCurrentEmployee(emp);
    setShowViewModal(true);
  };

  const handleEdit = (emp) => {
    setCurrentEmployee(emp);
    setShowEditModal(true);
  };

  const handleImageClick = (img, type) => {
    if (img && img !== "null") {
      setCurrentImage(img);
      setImageType(type);
      setShowImageModal(true);
    }
  };

  const handleViewCertificate = (certificateUrl, certificateName) => {
    setCurrentCertificate(certificateUrl);
    setCurrentCertificateName(certificateName);
    setShowCertificateModal(true);
  };

  const handleViewCertificatesList = () => {
    setShowCertificateListModal(true);
  };

  const parseCertificates = (certString) => {
    if (!certString || certString === "null") return [];
    try {
      const parsed = JSON.parse(certString);
      if (Array.isArray(parsed)) {
        if (typeof parsed[0] === "string") {
          return parsed.map((name) => ({ name, fileName: null }));
        }
        return parsed;
      }
      return [];
    } catch (e) {
      console.error("Error parsing certificates:", e);
      return [];
    }
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Error downloading file:", err);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Failed to download file",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );

  return (
    <div className="container mt-4">
      <Card className="p-3 mb-3 bg-light shadow-sm border-0">
        <Row className="align-items-center justify-content-center justify-content-md-start">
          <Col md={6}>
            <h4 className="mb-0 fw-semibold">Unapproved Employee Details</h4>
          </Col>

          <Col
            md={6}
            className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0"
          >
            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                size="sm"
                onClick={exportToExcel}
              >
                Export Excel
              </Button>
              <Button variant="outline-danger" size="sm" onClick={exportToPDF}>
                Export PDF
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {isMobile && (
        <div className="d-flex justify-content-end mb-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>
      )}

      <Card className={`p-3 mb-4 bg-light shadow-sm border-0 ${(showFilters ? "d-flex" : "d-none")}`}>
        <Row className="align-items-end g-3">
          <Col md={5} xs={12}>
            <Form.Label className="fw-semibold small mb-1">Search</Form.Label>
            <InputGroup size="sm">
              <InputGroup.Text>
                <Search size={16} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search here..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col md={3} xs={6}>
            <Form.Label className="fw-semibold small mb-1">
              Start Date
            </Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              placeholder="Start Date"
            />
          </Col>

          <Col md={3} xs={6}>
            <Form.Label className="fw-semibold small mb-1">End Date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              placeholder="End Date"
              min={startDateFilter}
            />
          </Col>

          {(startDateFilter || endDateFilter || searchTerm) && (
            <Col md={1} xs={12}>
              <Form.Label className="fw-semibold small mb-1 text-light">
                Clear
              </Form.Label>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  setStartDateFilter("");
                  setEndDateFilter("");
                  setSearchTerm("");
                }}
                className="form-control"
              >
                Clear
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      {filteredEmployees.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="d-none d-md-block">
            <Table striped bordered hover responsive>
              <thead id="text-white-important">
                <tr id="text-white-important">
                  <th id="text-white-important">S.I</th>
                  <th id="text-white-important">Employee ID</th>
                  <th id="text-white-important">Full Name</th>
                  <th id="text-white-important">Mobile</th>
                  <th id="text-white-important">Department</th>
                  <th id="text-white-important">Job Title</th>
                  <th id="text-white-important">Requested At</th>
                  <th id="text-white-important">Status</th>
                  <th id="text-white-important">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentEmployees.toReversed().map((emp, index) => (
                  <tr key={emp.id}>
                    <td>{filteredEmployees.length - index}</td>
                    <td>{display(emp.employeeId)}</td>
                    <td>{display(emp.fullName)}</td>
                    <td>{display(emp.personalMobile)}</td>
                    <td>{display(emp.department)}</td>
                    <td>{display(emp.jobTitle)}</td>
                    <td>{display(emp.createdAt)}</td>
                    <td>Pending</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handleView(emp)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => handleEdit(emp)}
                      >
                        Approve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Mini-Table View */}
          <div className="d-md-none mt-0">
            {currentEmployees.toReversed().map((emp, index) => (
              <div
                className="card shadow-sm"
                style={{ textAlign: "left", borderRadius: "12px" }}
              >
                <div className="card-body table-responsive">
                  <table
                    className="table table-striped table-bordered mb-2"
                    key={emp.id}
                  >
                    <tbody>
                      <tr>
                        <td>
                          <strong>SI</strong>
                        </td>
                        <td>{filteredEmployees.length - index}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Employee ID</strong>
                        </td>
                        <td>{display(emp.employeeId)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Full Name</strong>
                        </td>
                        <td>{display(emp.fullName)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Department</strong>
                        </td>
                        <td>{display(emp.department)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Job Title</strong>
                        </td>
                        <td>{display(emp.jobTitle)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Requested At</strong>
                        </td>
                        <td>{display(emp.createdAt)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Mobile</strong>
                        </td>
                        <td>{display(emp.personalMobile)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Status</strong>
                        </td>
                        <td>Pending</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Certifications</strong>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => handleViewCertificatesList(emp)}
                          >
                            View Certificates ({emp.certCount || 1})
                          </Button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="2" className="text-end">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => handleView(emp)}
                          >
                            View Details
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => handleEdit(emp)}
                          >
                            Approve
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <Alert variant="info">No unapproved employees found.</Alert>
      )}

      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Employee Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEmployee && (
            <>
              {/* PERSONAL INFO */}
              <h6 className="text-secondary">Personal Information</h6>
              <Table bordered responsive>
                <tbody>
                  <tr>
                    <th>Full Name</th>
                    <td>{display(currentEmployee.fullName)}</td>
                    <th>Employee ID</th>
                    <td>{display(currentEmployee.employeeId)}</td>
                  </tr>
                  <tr>
                    <th>Mobile</th>
                    <td>{display(currentEmployee.personalMobile)}</td>
                    <th>Email</th>
                    <td>{display(currentEmployee.personalEmail)}</td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td>{display(currentEmployee.gender)}</td>
                    <th>Date of Birth</th>
                    <td>{formatDate(currentEmployee.dateOfBirth)}</td>
                  </tr>
                  <tr>
                    <th>Nationality</th>
                    <td>{display(currentEmployee.nationality)}</td>
                    <th>Blood Group</th>
                    <td>{display(currentEmployee.bloodGroup)}</td>
                  </tr>
                </tbody>
              </Table>

              {/* EMPLOYMENT */}
              <h6 className="text-secondary mt-3">Employment Information</h6>
              <Table bordered responsive>
                <tbody>
                  <tr>
                    <th>Department</th>
                    <td>{display(currentEmployee.department)}</td>
                    <th>Job Title</th>
                    <td>{display(currentEmployee.jobTitle)}</td>
                  </tr>
                  <tr>
                    <th>Reporting Manager</th>
                    <td>{display(currentEmployee.reportingManager)}</td>
                    <th>Date of Joining</th>
                    <td>{formatDate(currentEmployee.dateOfJoining)}</td>
                  </tr>
                  <tr>
                    <th>Work Location</th>
                    <td>{display(currentEmployee.workLocation)}</td>
                    <th>Employment Type</th>
                    <td>{display(currentEmployee.employmentType)}</td>
                  </tr>
                </tbody>
              </Table>

              {/* EDUCATION */}
              <h6 className="text-secondary mt-3">Education & Skills</h6>
              <Table bordered responsive>
                <tbody>
                  <tr>
                    <th>Qualification</th>
                    <td>{display(currentEmployee.highestQualification)}</td>
                    <th>Specialization</th>
                    <td>{display(currentEmployee.specialization)}</td>
                  </tr>
                  <tr>
                    <th>University</th>
                    <td>{display(currentEmployee.university)}</td>
                    <th>Year of Passing</th>
                    <td>{display(currentEmployee.yearOfPassing)}</td>
                  </tr>
                  <tr>
                    <th>Certifications</th>
                    <td colSpan="3">
                      {currentEmployee.certifications &&
                        currentEmployee.certifications !== "null" ? (
                        (() => {
                          const certs = parseCertificates(
                            currentEmployee.certifications
                          );
                          if (certs.length === 0) {
                            return (
                              <span className="text-muted">
                                No certificates available
                              </span>
                            );
                          }
                          return (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleViewCertificatesList}
                            >
                              View Certificates ({certs.length})
                            </Button>
                          );
                        })()
                      ) : (
                        <span className="text-muted">
                          No certificates available
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>

              {/* BANK */}
              <h6 className="text-secondary mt-3">Bank Details</h6>
              <Table bordered responsive>
                <tbody>
                  <tr>
                    <th>Bank Name</th>
                    <td>{display(currentEmployee.bankName)}</td>
                    <th>Account Number</th>
                    <td>{display(currentEmployee.accountNumber)}</td>
                  </tr>
                  <tr>
                    <th>IFSC Code</th>
                    <td>{display(currentEmployee.ifscCode)}</td>
                    <th>Branch</th>
                    <td>{display(currentEmployee.branchName)}</td>
                  </tr>
                </tbody>
              </Table>

              <h6 className="text-secondary mt-3">Work Experience</h6>
              <Table bordered responsive>
                <tbody>
                  <tr>
                    <th style={{ width: "25%" }}>Previous Company</th>
                    <td style={{ width: "25%" }}>
                      {display(currentEmployee.previousCompany)}
                    </td>

                    <th style={{ width: "25%" }}>Previous Designation</th>
                    <td style={{ width: "25%" }}>
                      {display(currentEmployee.previousDesignation)}
                    </td>
                  </tr>

                  <tr>
                    <th>Previous Duration</th>
                    <td>{display(currentEmployee.previousDuration)}</td>

                    <th>Leaving Reason</th>
                    <td>{display(currentEmployee.leavingReason)}</td>
                  </tr>
                </tbody>
              </Table>


              {/* EMERGENCY */}
              <h6 className="text-secondary mt-3">Emergency Contact</h6>
              <Table bordered responsive>
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{display(currentEmployee.emergencyContactName)}</td>
                    <th>Relation</th>
                    <td>{display(currentEmployee.emergencyRelation)}</td>
                  </tr>
                  <tr>
                    <th>Mobile</th>
                    <td>{display(currentEmployee.emergencyMobile)}</td>
                    <th>Address</th>
                    <td>{display(currentEmployee.emergencyAddress)}</td>
                  </tr>
                </tbody>
              </Table>

              {/* IMAGES */}
              <h6 className="text-secondary mt-3">Photograph/Signature</h6>
              <div className="d-flex flex-wrap gap-3">
                {currentEmployee.photograph && (
                  <div className="text-center">
                    <Image
                      src={`https://api.dremerz.net/uploads/${currentEmployee.photograph}`}
                      thumbnail
                      style={{ width: "120px", cursor: "pointer" }}
                      onClick={() =>
                        handleImageClick(
                          currentEmployee.photograph,
                          "Photograph"
                        )
                      }
                    />
                    <p className="small">Photograph</p>
                  </div>
                )}
                {currentEmployee.signature && (
                  <div className="text-center">
                    <Image
                      src={`https://api.dremerz.net/uploads/${currentEmployee.signature}`}
                      thumbnail
                      style={{ width: "120px", cursor: "pointer" }}
                      onClick={() =>
                        handleImageClick(currentEmployee.signature, "Signature")
                      }
                    />
                    <p className="small">Signature</p>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={handleApprove}>
            Confirm Approval
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Approve Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to approve{" "}
            <strong>{currentEmployee?.fullName}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApprove}>
            Confirm Approval
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{imageType}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {currentImage && (
            <Image
              src={`https://api.dremerz.net/uploads/${currentImage}`}
              fluid
              style={{ maxHeight: "70vh", height: "500px", width: "500px" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() =>
              handleDownload(
                `https://api.dremerz.net/uploads/${currentImage}`,
                `${imageType || "image"}.jpg`
              )
            }
          >
            Download {imageType}
          </Button>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Certificate List Modal */}
      <Modal
        show={showCertificateListModal}
        onHide={() => setShowCertificateListModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Certificates - {currentEmployee?.fullName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEmployee &&
            (() => {
              const certificates = parseCertificates(
                currentEmployee.certifications
              );

              if (certificates.length === 0) {
                return (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-certificate fa-2x mb-3"></i>
                    <p>No certificates available</p>
                  </div>
                );
              }

              return (
                <div className="certificate-list">
                  {certificates.map((cert, index) => {
                    const certificateKey = `certificate_${index}`;
                    const certificateFileName = currentEmployee[certificateKey];

                    return (
                      <div
                        key={index}
                        className="border rounded p-3 mb-3 bg-light d-flex justify-content-between align-items-center"
                      >
                        <div className="flex-grow-1">
                          <div className="fw-medium mb-1">
                            {`Certificate ${index + 1}`}
                          </div>
                        </div>
                        {certificateFileName &&
                          certificateFileName !== "null" ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setShowCertificateListModal(false);
                              const fullUrl = `https://api.dremerz.net/uploads/${certificateFileName}`;
                              handleViewCertificate(
                                fullUrl,
                                cert.name || `Certificate ${index + 1}`
                              );
                            }}
                          >
                            <i className="fas fa-eye me-1"></i>
                            View
                          </Button>
                        ) : (
                          // <Badge bg="secondary">Not Available</Badge>
                          <></>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCertificateListModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Certificate View Modal */}
      <Modal
        show={showCertificateModal}
        onHide={() => {
          setShowCertificateModal(false);
          setTimeout(() => setShowCertificateListModal(true), 100);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{currentCertificateName}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "70vh", overflow: "auto" }}>
          {currentCertificate &&
            (currentCertificate.toLowerCase().endsWith(".pdf") ? (
              <iframe
                src={currentCertificate}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="Certificate"
              />
            ) : (
              <img
                src={currentCertificate}
                alt="Certificate"
                style={{ width: "100%", height: "auto" }}
              />
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() =>
              handleDownload(
                currentCertificate,
                currentCertificateName || "Certificate.pdf"
              )
            }
          >
            Download
          </Button>

          <Button
            variant="secondary"
            onClick={() => {
              setShowCertificateModal(false);
              setTimeout(() => setShowCertificateListModal(true), 100);
            }}
          >
            Back to List
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeeDetailsUnapproved;
